'use client';

import { useModals } from '@/contexts/ModalContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { IconChevronLeft } from '@tabler/icons-react';
import { useCallback, useState } from 'react';
import {
  LocationSelectorEmbedded,
  LocationSelectorResponse,
} from '@/app/[lang]/(main)/components/shared/LocationSelector';
import { useSetLocation } from '@/contexts/CookieContext';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

type Props = {
  mode: 'detected' | 'selected';
  countryName: string;
  countryLink?: string;
  countryCode: string;
  hasWhiteLabel: boolean;
  onGo?: () => void;
};

const getCountryFlag = (countryCode: string) => {
  return `/assets/icons/flags/${countryCode.toLowerCase()}.svg`;
};

const getFirstCharUpper = (text?: string) => {
  if (!text) {
    return '';
  }
  return text[0].toUpperCase() + text.slice(1);
};

export const ChangeWLModal: React.FC<Props> = ({
  countryName,
  countryLink,
  countryCode,
  mode = 'detected',
  hasWhiteLabel = true,
  onGo,
}) => {
  const modals = useModals();
  const [isGeoSelectionOpen, setIsGeoSelectionOpen] = useState(false);
  const t = useTranslations('modals.wlModal');

  const updateLocation = useSetLocation();

  const onClose = useCallback(() => {
    modals.close(`wl-routing-modal`);
  }, [modals]);

  const onClickGo = useCallback(() => {
    if (countryLink) {
      const targetUrl = countryLink.startsWith('http') ? countryLink : `https://${countryLink}`;
      modals.close(`wl-routing-modal`);
      window.location.assign(targetUrl);
      return;
    }
    onGo?.();
    modals.close(`wl-routing-modal`);
  }, [countryLink, modals, onGo]);

  const onOpenGeoSelection = useCallback(() => {
    setIsGeoSelectionOpen(true);
  }, []);

  const onChangeLocation = useCallback(
    (location: LocationSelectorResponse) => {
      modals.close(`wl-routing-modal`);
      updateLocation(location.countryId ?? '', location.regionId ?? '', location.cityId ?? '', {
        lat: location.latitude ?? 0,
        lon: location.longitude ?? 0,
      });
      setIsGeoSelectionOpen(false);
    },
    [modals, updateLocation],
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent showCloseButton={false}>
        {isGeoSelectionOpen ? (
          <>
            <Button
              variant='ghost'
              onClick={() => setIsGeoSelectionOpen(false)}
              className='self-start size-[32px] p-0 rounded-full'
            >
              <IconChevronLeft className='text-text-primary' />
            </Button>
            <LocationSelectorEmbedded
              className={'mt-[26px]'}
              defaultState={undefined}
              onChange={onChangeLocation}
              showBackButton={false}
            />
          </>
        ) : (
          <>
            <DialogHeader className='items-center text-center'>
              <DialogTitle>
                {mode === 'detected' ? t('title_detected', { countryName }) : t('title_selected', { countryName })}
              </DialogTitle>
              <DialogDescription>
                {hasWhiteLabel ? t('subtitle_has_wl', { countryName }) : t('subtitle_no_wl')}
              </DialogDescription>
            </DialogHeader>

            <div className='flex flex-col gap-[8px] w-full'>
              <Button
                variant='outline'
                onClick={onClickGo}
                className='h-auto p-[15px] w-full gap-[8px] rounded-[12px] bg-bg-day hover:bg-bg-day text-m font-semibold! border-primary-green-day'
              >
                {hasWhiteLabel
                  ? t('button_go', { countryName: getFirstCharUpper(countryLink) || '' })
                  : t('button_show')}
                <Image src={getCountryFlag(countryCode)} width={30} height={30} alt='' />
              </Button>

              <Button
                variant='outline'
                onClick={onOpenGeoSelection}
                className='h-auto p-[15px] w-full gap-[8px] rounded-[12px] bg-bg-day hover:bg-bg-day font-semibold! text-m border-primary-green-day'
              >
                {t('button_choose_another')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
